import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ProjectCard from "@/components/ProjectCard";
import Logo from "@/components/Logo";
import { Plus } from "@phosphor-icons/react";

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const filtered = projects.filter((p) => {
    if (filter === "all") return true;
    return (p.state || p.bucket) === filter;
  });

  const SORTS = {
    hardest_first: (a, b) => {
      const rank = { hard: 0, medium: 1, easy: 2, undefined: 3, null: 3 };
      return (rank[a.ai_meta?.difficulty] ?? 3) - (rank[b.ai_meta?.difficulty] ?? 3);
    },
    newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
    oldest: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
    closest_done: (a, b) => (b.progress_pct ?? 0) - (a.progress_pct ?? 0),
    least_done: (a, b) => (a.progress_pct ?? 0) - (b.progress_pct ?? 0),
    time_remaining: (a, b) => {
      const ar = (a.ai_meta?.estimated_minutes ?? 60) * (1 - (a.progress_pct ?? 0) / 100);
      const br = (b.ai_meta?.estimated_minutes ?? 60) * (1 - (b.progress_pct ?? 0) / 100);
      return ar - br;
    },
  };
  const hasAnyDifficulty = projects.some((p) => p.ai_meta?.difficulty);
  const [sort, setSort] = useState(hasAnyDifficulty ? "hardest_first" : "newest");
  const sortedFiltered = [...filtered].sort(SORTS[sort] || SORTS.newest);

  const sortOptions = [
    { k: "hardest_first", label: "Hardest first" },
    { k: "newest", label: "Newest" },
    { k: "oldest", label: "Oldest" },
    { k: "closest_done", label: "Closest to done" },
    { k: "least_done", label: "Least complete" },
    { k: "time_remaining", label: "Time remaining" },
  ];

  const filters = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "awaiting_materials", label: "Awaiting materials" },
    { key: "paused", label: "Paused" },
    { key: "complete", label: "Done" },
  ];

  return (
    <div className="min-h-screen pb-32" data-testid="projects-page">
      <header className="px-5 pt-6 pb-3 max-w-md mx-auto w-full flex items-center justify-between">
        <Logo />
        <button
          onClick={() => navigate("/projects/new")}
          data-testid="projects-new-btn"
          className="bg-timber-900 text-masking rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-1 btn-press shadow-tactile"
        >
          <Plus size={18} weight="bold" /> New
        </button>
      </header>

      <div className="px-5 max-w-md mx-auto w-full">
        <h1 className="font-heading text-3xl font-bold">Projects</h1>
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2 -mx-1 px-1" data-testid="projects-filters">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full border-2 font-bold text-sm whitespace-nowrap ${
                filter === f.key
                  ? "bg-timber-900 border-timber-900 text-masking"
                  : "bg-chalk border-stone_border text-timber-700"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 mt-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="projects-sort">
          {sortOptions.map((s) => (
            <button
              key={s.k}
              data-testid={`sort-${s.k}`}
              onClick={() => setSort(s.k)}
              className={`px-3 py-1.5 rounded-full border-2 font-semibold text-xs whitespace-nowrap ${
                sort === s.k
                  ? "bg-denim border-denim text-chalk"
                  : "bg-chalk border-stone_border text-timber-700"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <main className="px-5 max-w-md mx-auto w-full mt-4 space-y-3">
        {sortedFiltered.length === 0 ? (
          <p className="text-sm text-timber-500">No projects in this view.</p>
        ) : sortedFiltered.map((p) => <ProjectCard key={p.id} project={p} />)}
      </main>
    </div>
  );
}
